<template>
    <v-container fluid class="pa-0">
        <v-toolbar dark color="primary">
            <template v-slot:img>
                <v-img :src="require('@/assets/banner.png')"></v-img>
            </template>
            <v-toolbar-title class="title">Admin</v-toolbar-title>
            <v-divider vertical inset class="mx-3"></v-divider>
            <v-toolbar-items>
                <v-btn text dark @click="load({ feature: 'none', sectors: [], functionalAreas: [] })"><v-icon left>mdi-plus</v-icon>Add Standard</v-btn>
                <v-btn text dark @click="load({ feature: 'none', sectors: [], functionalAreas: [], status: 'Area of Need', relatedEfforts: [{ text: null, link: null, scb: null }] })"><v-icon left>mdi-plus</v-icon>Add Area of Need</v-btn>
            </v-toolbar-items>
            <v-spacer></v-spacer>
            <v-toolbar-title class="title">The Regenerative Medicine Standards Portal</v-toolbar-title>
            <v-divider vertical inset class="mx-3"></v-divider>
            <v-toolbar-items>
                <v-btn text dark @click="logout()"><v-icon left>mdi-logout</v-icon>Logout</v-btn>
            </v-toolbar-items>
        </v-toolbar>
        <v-container fluid>
            <div>
                <v-text-field v-model="text" @input="searchDebounced()" :messages="results" prepend-inner-icon="mdi-magnify" clearable filled rounded>
                    <template v-slot:message="msg">
                        <div class="text-right">{{ msg.message }}</div>
                    </template>
                </v-text-field>
            </div>
            <div v-if="standards && standards.length">
                <v-data-table :headers="headers" :sort-by="'updatedAt'" :sort-desc="true" :custom-sort="customSort" :items="standards" item-key="hex" disable-pagination hide-default-footer @click:row="load" class="clickable-rows">
                    <template v-slot:item.status="{ item }">
                        <div class="text-no-wrap">{{ item.statusText }}</div>
                        <div class="text-no-wrap grey--text text--lighten-1">{{ item.statusDate }}</div>
                    </template>
                    <template v-slot:item.feature="{ item }">
                        <v-icon v-if="item.activeFeatureIcon" color="primary">${{ item.activeFeatureIcon }}</v-icon>
                    </template>
                    <template v-slot:item.updatedAt="{ item }">
                        <div class="text-no-wrap">{{ item.updatedAt | date('LLL') }}</div>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-icon @click.stop="del(item)">mdi-delete</v-icon>
                    </template>
                </v-data-table>
            </div>
        </v-container>
        <v-dialog v-if="standard" v-model="standard" fullscreen hide-overlay persistent transition="dialog-bottom-transition">
            <v-card>
                <v-toolbar dark color="primary">
                    <v-row justify="space-between" align="center">
                        <v-col>
                            <div class="d-flex align-center">
                                <v-btn icon dark @click="close()" :disabled="modified">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                                <v-toolbar-title class="mx-3">{{ standard.hex ? 'Edit' : 'Add' }} {{ 'Area of Need' === standard.status ? 'Area of Need' : 'Standard' }}</v-toolbar-title>
                            </div>
                        </v-col>
                        <v-col cols="auto">
                            <span class="caption" v-if="standard.updatedAt"><em class="grey--text text--lighten-1">Last Updated:</em> {{ standard.updatedAt | date('LLL') }}</span>
                        </v-col>
                        <v-col class="text-right">
                            <span class="caption mx-3" v-if="!valid">Missing required* field(s)</span>
                            <v-btn text dark @click="save()" :disabled="!modified || !valid"><v-icon left>mdi-content-save</v-icon>Save</v-btn>
                            <v-btn text dark @click="undo()" :disabled="!modified"><v-icon left>mdi-undo</v-icon>Cancel</v-btn>
                        </v-col>
                    </v-row>
                </v-toolbar>
                <v-container fluid>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field dense hide-details v-model="standard.title" label="Title*" outlined></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row v-if="'Area of Need' !== standard.status">
                        <v-col cols="3">
                            <v-text-field dense hide-details v-model="standard.standardId" label="ID" outlined></v-text-field>
                        </v-col>
                        <v-col cols="9">
                            <v-combobox dense hide-details v-model="standard.sdo" :items="sdos" label="SDO*" outlined></v-combobox>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="3">
                            <v-card class="mb-3" v-if="'Area of Need' !== standard.status">
                                <v-toolbar dark dense color="primary">
                                    <v-toolbar-title>Status*</v-toolbar-title>
                                </v-toolbar>
                                <v-card-text>
                                    <v-container fluid ma-0 pa-0>
                                        <v-row class="my-n3">
                                            <v-col>
                                                <v-radio-group dense hide-details v-model="standard.status">
                                                    <v-radio v-for="(status, index) in statuses" :key="index" :label="statusDisplay(status)" :value="status"></v-radio>
                                                </v-radio-group>
                                            </v-col>
                                            <v-col>
                                                <v-select dense hide-details v-model="standard[`${camelCase(standard.status)}Month`]" :items="months" label="Month" outlined clearable class="mb-3"></v-select>
                                                <v-text-field dense hide-details v-model="standard[`${camelCase(standard.status)}Year`]" label="Year" maxlength="4" v-mask="'####'" outlined></v-text-field>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                            <v-card class="mb-3" v-if="'Area of Need' !== standard.status">
                                <v-toolbar dark dense color="primary">
                                    <v-toolbar-title>Type*</v-toolbar-title>
                                </v-toolbar>
                                <v-card-text>
                                    <v-radio-group dense hide-details v-model="standard.type">
                                        <v-radio v-for="(type, index) in types" :key="index" :label="type" :value="type"></v-radio>
                                    </v-radio-group>
                                </v-card-text>
                            </v-card>
                            <v-card class="mb-3">
                                <v-toolbar dark dense color="primary">
                                    <v-toolbar-title>Sectors*</v-toolbar-title>
                                </v-toolbar>
                                <v-card-text>
                                    <div v-for="(sector, index) in sectors" :key="index">
                                        <v-checkbox dense hide-details v-model="standard.sectors" :label="sector" :value="sector"></v-checkbox>
                                    </div>
                                </v-card-text>
                            </v-card>
                            <v-card class="mb-3">
                                <v-toolbar dark dense color="primary">
                                    <v-toolbar-title>Functional Areas*</v-toolbar-title>
                                </v-toolbar>
                                <v-card-text>
                                    <div v-for="(functionalArea, index) in functionalAreas" :key="index">
                                        <v-checkbox dense hide-details v-model="standard.functionalAreas" :label="functionalArea" :value="functionalArea"></v-checkbox>
                                    </div>
                                </v-card-text>
                            </v-card>
                            <v-card class="mb-3" v-if="'Area of Need' === standard.status">
                                <v-toolbar dark dense color="primary">
                                    <v-toolbar-title>Current Priority*</v-toolbar-title>
                                </v-toolbar>
                                <v-card-text>
                                    <v-radio-group dense hide-details v-model="standard.priority">
                                        <v-radio v-for="(priority, index) in priorities" :key="index" :label="priority" :value="priority"></v-radio>
                                    </v-radio-group>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col>
                            <v-card class="mb-5">
                                <v-toolbar dark dense color="primary">
                                    <v-toolbar-title>Feature</v-toolbar-title>
                                </v-toolbar>
                                <v-card-text>
                                    <v-radio-group dense hide-details v-model="standard.feature" row>
                                        <v-radio v-for="(feature, index) in filteredFeatures" :key="index" :value="feature.value" @change="featureChanged(feature.value)">
                                            <template v-slot:label>
                                                <v-icon left v-if="feature.value !== 'none'" :class="{ 'primary--text': feature.value === standard.feature }">${{ featureIcon(feature.value, standard.type) }}</v-icon>
                                                {{ feature.text }}
                                            </template>
                                        </v-radio>
                                        <v-spacer></v-spacer>
                                        <DateField v-model="standard.featureExpiration" label="Expiration" :disabled="'none' === standard.feature"></DateField>
                                    </v-radio-group>
                                </v-card-text>
                            </v-card>
                            <v-row>
                                <v-col>
                                    <v-textarea dense auto-grow v-model="standard.description" label="Description*" outlined></v-textarea>
                                </v-col>
                                <v-col>
                                    <v-card outlined class="pa-2">
                                        <Markdown :text="standard.description" :track="false"></Markdown>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-text-field dense v-model="standard.keywords" label="Additional Keywords" outlined></v-text-field>
                            <v-row v-if="'Area of Need' === standard.status">
                                <v-col>
                                    <v-textarea dense auto-grow v-model="standard.challenge" label="Challenges*" outlined></v-textarea>
                                </v-col>
                                <v-col>
                                    <v-card outlined class="pa-2">
                                        <Markdown :text="standard.challenge" :track="false"></Markdown>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-row v-if="'Area of Need' === standard.status">
                                <v-col>
                                    <v-textarea dense auto-grow v-model="standard.objective" label="Standard Solution*" outlined></v-textarea>
                                </v-col>
                                <v-col>
                                    <v-card outlined class="pa-2">
                                        <Markdown :text="standard.objective" :track="false"></Markdown>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-row v-if="'Area of Need' === standard.status">
                                <v-col>
                                    <v-textarea dense auto-grow v-model="standard.areasToStandardize" label="Potential Areas to Standardize*" outlined></v-textarea>
                                </v-col>
                                <v-col>
                                    <v-card outlined class="pa-2">
                                        <Markdown :text="standard.areasToStandardize" :track="false"></Markdown>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <div class="mb-6" v-if="'Area of Need' === standard.status">
                                <div class="caption">Related Efforts*</div>
                                <v-row no-gutters class="mb-2 align-center" v-for="(relatedEffort, index) in standard.relatedEfforts" :key="index">
                                    <v-col class="mr-1">
                                        <v-text-field dense hide-details v-model="relatedEffort.text" label="Text" outlined></v-text-field>
                                    </v-col>
                                    <v-col class="mr-1">
                                        <v-text-field dense hide-details v-model="relatedEffort.link" label="Link" outlined></v-text-field>
                                    </v-col>
                                    <v-col cols="auto" class="mr-1">
                                        <v-checkbox dense hide-details v-model="relatedEffort.scb" label="SCB?"></v-checkbox>
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-btn icon @click="standard.relatedEfforts.splice(index, 1)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-btn small color="primary" @click="standard.relatedEfforts.push({ text: null, link: null, scb: null })">
                                    <v-icon left>mdi-plus</v-icon>
                                    Add Related Effort
                                </v-btn>
                            </div>
                            <v-row>
                                <v-col>
                                    <v-textarea dense auto-grow v-model="standard.callToAction" :label="'Area of Need' === standard.status ? 'Community Calls to Action*' : 'Updates and Calls to Action'" outlined></v-textarea>
                                </v-col>
                                <v-col>
                                    <v-card outlined class="pa-2">
                                        <Markdown :text="standard.callToAction" :track="false"></Markdown>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-checkbox dense class="mb-3" v-model="standard.fda" label="FDA Recognized" v-if="'Area of Need' !== standard.status"></v-checkbox>
                            <v-text-field dense v-model="standard.availability" label="Availability" outlined v-if="'Area of Need' !== standard.status"></v-text-field>
                            <v-row v-if="'Area of Need' !== standard.status">
                                <v-col>
                                    <v-textarea dense auto-grow v-model="standard.bundles" label="Packages" outlined></v-textarea>
                                </v-col>
                                <v-col>
                                    <v-card outlined class="pa-2">
                                        <Markdown :text="standard.bundles" :track="false"></Markdown>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-textarea dense auto-grow v-model="standard.note" label="Admin Notes" outlined></v-textarea>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card>
        </v-dialog>
        <ConfirmDialog ref="confirm"></ConfirmDialog>
    </v-container>
</template>

<script>
    import ConfirmDialog from '@/components/ConfirmDialog';
    import DateField from '@/components/DateField';
    import Markdown from '@/components/Markdown';
    import { statuses, sectors, functionalAreas, types, priorities, features, icon, statusDisplay } from '@/common';
    import { camelCase, cloneDeep, debounce, reverse, sortBy, toUpper } from 'lodash';
    import moment from 'moment';
    import router from '@/router';
    import api from '@/api';

    export default {
        name: 'Admin',
        components: { ConfirmDialog, DateField, Markdown },
        data: () => ({
            user: null,
            months: moment.months(),
            headers: [
                { text: 'Organization', value: 'sdo' },
                { text: 'ID', value: 'standardId' },
                { text: 'Title', value: 'title' },
                { text: 'Status', value: 'status' },
                { text: 'Type', value: 'type' },
                { text: '', value: 'feature', sortable: false },
                { text: 'Last Updated', value: 'updatedAt' },
                { text: '', value: 'actions', sortable: false }
            ],
            text: '',
            sdos: [],
            statuses: statuses,
            sectors: sectors,
            functionalAreas: functionalAreas,
            types: types,
            priorities: priorities,
            standards: [],
            cleanStandard: null,
            standard: null
        }),
        computed: {
            filteredFeatures() {
                if ('Area of Need' === this.standard.status) {
                    return [features[0], features[2], features[3]];
                }
                return features;
            },
            results() {
                return `${this.standards.length} ${1 === this.standards.length ? 'entry' : 'entries'} found`;
            },
            valid() {
                return (
                    this.standard.title &&
                    (this.standard.sdo || 'Area of Need' === this.standard.status) &&
                    this.standard.status &&
                    (this.standard.type || 'Area of Need' === this.standard.status) &&
                    this.standard.sectors?.length &&
                    this.standard.functionalAreas?.length &&
                    this.standard.description &&
                    (this.standard.challenge || 'Area of Need' !== this.standard.status) &&
                    (this.standard.objective || 'Area of Need' !== this.standard.status) &&
                    (this.standard.areasToStandardize || 'Area of Need' !== this.standard.status) &&
                    ((this.standard.relatedEfforts?.length && this.standard.relatedEfforts[0].text) || 'Area of Need' !== this.standard.status) &&
                    (this.standard.callToAction || 'Area of Need' !== this.standard.status)
                );
            },
            modified() {
                return JSON.stringify(this.cleanStandard) !== JSON.stringify(this.standard);
            }
        },
        mounted() {
            this.lookupSDOs();
            this.search();
        },
        methods: {
            camelCase: camelCase,
            statusDisplay: statusDisplay,
            customSort(items, field, descending) {
                const _field = field[0];
                const _descending = descending[0];
                if (_field) {
                    const sorted = (() => {
                        if ('status' === _field) {
                            return sortBy(items, (standard) => {
                                const year = standard[`${camelCase(standard.status)}Year`];
                                const month = standard[`${camelCase(standard.status)}Month`];
                                let date = null;
                                if (year) {
                                    if (month) {
                                        date = moment(`${year} ${month}`, 'YYYY MMMM');
                                    } else {
                                        date = moment(`${year}`, 'YYYY');
                                    }
                                }
                                const secondary = Number.MAX_SAFE_INTEGER - (date ? date.unix() : 0);
                                return `${standard.statusText} ${secondary}`;
                            });
                        }
                        return sortBy(items, (standard) => {
                            return toUpper(standard[_field] || '');
                        });
                    })();
                    if (_descending) {
                        return reverse(sorted);
                    }
                    return sorted;
                }
                return items;
            },
            handleError(error) {
                if (401 === error.response.status) {
                    this.$refs.confirm.show('Session Timeout', 'Your session has timed out, please log in again', { showCancel: false }).finally(() => {
                        router.replace('/admin/login');
                    });
                } else {
                    this.$refs.confirm.show('Error', 'An unexpected error occurred, please try again', { showCancel: false });
                }
            },
            statusText(type) {
                return 'Documentary' === type ? 'Published' : 'Released';
            },
            featureIcon(feature, type) {
                return icon('available' === feature ? this.statusText(type) : feature);
            },
            featureChanged(val) {
                if ('none' === val) {
                    this.standard.featureExpiration = null;
                } else {
                    this.standard.featureExpiration = moment().add(45, 'days').toISOString();
                }
            },
            load(standard) {
                this.cleanStandard = cloneDeep(standard);
                this.standard = cloneDeep(this.cleanStandard);
            },
            del(standard) {
                this.$refs.confirm.show('Delete Confirmation', `Are you sure you want to delete this ${'Area of Need' === standard.status ? 'area of need' : 'standard'}?`).then((confirm) => {
                    if (confirm) {
                        this.$api
                            .delete(`/admin/delete/${standard.hex}`)
                            .then(() => {
                                this.search();
                            })
                            .catch(this.handleError);
                    }
                });
            },
            save() {
                this.$api
                    .post('/admin/save', this.standard)
                    .then((response) => {
                        this.load(response.data);
                    })
                    .catch(this.handleError);
            },
            undo() {
                this.standard = cloneDeep(this.cleanStandard);
            },
            close() {
                this.search().finally(() => {
                    this.cleanStandard = null;
                    this.standard = null;
                });
            },
            logout() {
                this.$api.get('/admin/logout').finally(() => {
                    window.location.href = '/admin';
                });
            },
            lookupSDOs() {
                this.$api.get('/lookup', { params: { field: 'sdo' } }).then((response) => {
                    this.sdos = response.data;
                });
            },
            search() {
                return this.$api.get('/search', { params: { text: this.text } }).then((response) => {
                    this.standards = response.data;
                });
            },
            searchDebounced: debounce(function () {
                this.search();
            }, 500)
        },
        beforeRouteEnter(to, from, next) {
            api.get('/admin/login')
                .then((response) => {
                    const user = response.data;
                    next((vm) => {
                        vm.user = user;
                    });
                })
                .catch(() => {
                    next({ name: 'admin-login', replace: true });
                });
        }
    };
</script>

<style scoped>
    ::v-deep .clickable-rows tbody tr {
        cursor: pointer;
    }
    ::v-deep .v-input--selection-controls {
        margin-top: 0;
        padding-top: 0;
    }
</style>
